// The second factor — `/v1/auth/two-factor/*` (spec/07 §2.1 `twoFactor`, §6.6; F-IAM-09).
//
// These endpoints are **better-auth's**, served by its handler at `/v1/auth/*`; nothing here mounts
// them. They are declared because the consoles and the staff app call them to get past the console
// gate, and a client generated from this document should be able to: enrol an authenticator, prove
// it on a session that has only a first factor, spend a backup code, and turn the factor off.
//
// They are the subset of better-auth's own that we support, described as better-auth 1.7.5 serves
// them, and `apps/server`'s second-factor suite checks every path, method and request field here
// against the schema better-auth itself generates — so an upgrade that renames a field fails a test
// rather than a console. Deliberately left out:
//
//   * `trustDevice` — a trusted device skips the second factor at the next password sign-in, and
//     a session made that way has **not** completed one, so it would not pass the console gate
//     anyway (spec/07 §6.6). Offering the box would be offering something that does nothing.
//   * `disableSession` on the backup code — a code spent without a session is a code spent for
//     nothing the gate can see.
//   * the email/SMS `otp` method — a code sent to the phone that signed in is not a second factor.
//
// Failures are better-auth's shape, `{ code, message }`, not problem+json; our own refusals in front
// of these endpoints use the same shape so a client handles both alike.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { JSON_CONTENT_TYPE, jsonResponse } from "../openapi/responses.js";
export const AuthErrorSchema = z
    .object({
    /** Stable, upper snake case: `INVALID_CODE`, `RATE_LIMITED`, `SECOND_FACTOR_REQUIRED`, `SESSION_NOT_FRESH`. */
    code: z.string(),
    message: z.string(),
})
    .openapi('AuthError', {
    description: 'A refusal from the authentication endpoints (better-auth’s shape, not problem+json).',
});
function authError(description, ...codes) {
    const suffix = codes.length > 0 ? ` Codes: ${codes.join(', ')}.` : '';
    return { description: `${description}${suffix}`, content: { [JSON_CONTENT_TYPE]: { schema: AuthErrorSchema } } };
}
const SESSION = 'Needs a session: `Authorization: Bearer <token>` in the apps, the session cookie in a browser.';
/**
 * A person banned from the Platform is refused every new session (spec/07 §3.5, F-IAM-07): better-auth's
 * `admin` plugin answers `BANNED_USER` wherever a session would be created — a sign-in, a phone code, an
 * email code, or the second factor that finishes a password sign-in. Only once the credential has
 * checked out, and never with the ban's reason.
 */
export const BANNED_USER_REFUSAL = authError('Banned from the Platform: the credential was right, and no session is created. "This account cannot sign in. ' +
    'Please contact support." — never the reason.', 'BANNED_USER');
export const TwoFactorEnableRequestSchema = z
    .object({
    /**
     * The account's password, when it has one. A phone-first account has none and enrols without
     * it — from a session signed in within the last ten minutes (spec/07 §2 `freshAge`, §6.6).
     */
    password: z.string().min(1).max(256).optional(),
    /** The label an authenticator app shows. Defaults to `Clavian`. */
    issuer: z.string().min(1).max(64).optional(),
})
    .openapi('TwoFactorEnableRequest');
export const TwoFactorEnrolmentSchema = z
    .object({
    method: z.enum(['totp']),
    /** `otpauth://totp/…` — render it as a QR code for the authenticator app. */
    totpURI: z.string(),
    /** Ten single-use recovery codes. Shown once; each works once. */
    backupCodes: z.array(z.string()),
})
    .openapi('TwoFactorEnrolment', {
    description: 'An authenticator to set up. It is not active until its first code is verified.',
});
export const TwoFactorCodeRequestSchema = z
    .object({ code: z.string().min(1).max(64) })
    .openapi('TwoFactorCodeRequest', { description: 'A six-digit authenticator code, or a backup code.' });
export const TwoFactorUserSchema = z
    .object({
    id: z.string(),
    twoFactorEnabled: z.boolean().optional(),
})
    .openapi('TwoFactorUser');
export const TwoFactorVerifiedSchema = z
    .object({
    /**
     * The session to use from now on. Verifying on a session that had only a first factor
     * **replaces** it: this is a new session that records the second factor, and the old token no
     * longer works (spec/07 §6.6). In a browser the new session cookie is set as well.
     */
    token: z.string(),
    user: TwoFactorUserSchema,
})
    .openapi('TwoFactorVerified', { description: 'The second factor is proven; the session records it.' });
export const TwoFactorPasswordRequestSchema = z
    .object({
    /** The account's password, when it has one. */
    password: z.string().min(1).max(256).optional(),
})
    .openapi('TwoFactorPasswordRequest');
export const TwoFactorStatusSchema = z.object({ status: z.boolean() }).openapi('TwoFactorStatus');
export const TwoFactorBackupCodesSchema = z
    .object({ status: z.boolean(), backupCodes: z.array(z.string()) })
    .openapi('TwoFactorBackupCodes', { description: 'A new set of backup codes; the old set no longer works.' });
const json = (schema) => ({
    body: { content: { [JSON_CONTENT_TYPE]: { schema } }, required: true },
});
export const enableTwoFactorRoute = createRoute({
    method: 'post',
    path: '/v1/auth/two-factor/enable',
    operationId: 'enableTwoFactor',
    tags: ['identity'],
    summary: 'Start setting up an authenticator app',
    description: `${SESSION} Returns the secret as an \`otpauth://\` URI and ten backup codes. Nothing is enforced until ` +
        'the first code is verified at `/two-factor/verify-totp`. An account with a password must give it; one ' +
        'without must have signed in within the last ten minutes. With a second factor already on, the session ' +
        'must have completed it.',
    request: json(TwoFactorEnableRequestSchema),
    responses: {
        200: jsonResponse(TwoFactorEnrolmentSchema, 'The authenticator to set up.'),
        400: authError('The password is wrong, or an authenticator is already set up.', 'INVALID_PASSWORD', 'TOTP_ALREADY_ENABLED'),
        401: authError('Not signed in.', 'UNAUTHORIZED'),
        403: authError('The change is not proven.', 'SECOND_FACTOR_REQUIRED', 'SESSION_NOT_FRESH'),
    },
});
export const verifyTwoFactorTotpRoute = createRoute({
    method: 'post',
    path: '/v1/auth/two-factor/verify-totp',
    operationId: 'verifyTwoFactorTotp',
    tags: ['identity'],
    summary: 'Prove the second factor with an authenticator code',
    description: 'Three uses: confirming a new authenticator; finishing a password sign-in that stopped at the second ' +
        'factor (with the `two_factor` cookie that sign-in set); and **stepping up** a session that has only a ' +
        'first factor — a phone code, say — which is what the console routes ask for (`second_factor_required`). ' +
        'The session that comes back records the factor. Five attempts per five minutes per account.',
    request: json(TwoFactorCodeRequestSchema),
    responses: {
        200: jsonResponse(TwoFactorVerifiedSchema, 'Proven.'),
        400: authError('No authenticator is set up.', 'TOTP_NOT_ENABLED'),
        401: authError('The code is wrong, or there is no session or sign-in to prove it for.', 'INVALID_CODE', 'INVALID_TWO_FACTOR_COOKIE'),
        403: BANNED_USER_REFUSAL,
        429: authError('Five attempts in five minutes for this account.', 'RATE_LIMITED'),
    },
});
export const verifyTwoFactorBackupCodeRoute = createRoute({
    method: 'post',
    path: '/v1/auth/two-factor/verify-backup-code',
    operationId: 'verifyTwoFactorBackupCode',
    tags: ['identity'],
    summary: 'Prove the second factor with a backup code',
    description: 'As `/two-factor/verify-totp`, with one of the backup codes instead. Each code works once and is then ' +
        'spent. Counts towards the same five attempts per five minutes per account.',
    request: json(TwoFactorCodeRequestSchema),
    responses: {
        200: jsonResponse(TwoFactorVerifiedSchema, 'Proven; the code is spent.'),
        400: authError('No backup codes are set up.', 'BACKUP_CODES_NOT_ENABLED'),
        401: authError('The code is wrong or already spent.', 'INVALID_BACKUP_CODE', 'INVALID_TWO_FACTOR_COOKIE'),
        403: BANNED_USER_REFUSAL,
        429: authError('Five attempts in five minutes for this account.', 'RATE_LIMITED'),
    },
});
export const generateTwoFactorBackupCodesRoute = createRoute({
    method: 'post',
    path: '/v1/auth/two-factor/generate-backup-codes',
    operationId: 'generateTwoFactorBackupCodes',
    tags: ['identity'],
    summary: 'Replace the backup codes',
    description: `${SESSION} The session must have completed the second factor. The old codes stop working.`,
    request: json(TwoFactorPasswordRequestSchema),
    responses: {
        200: jsonResponse(TwoFactorBackupCodesSchema, 'The new codes.'),
        400: authError('The password is wrong.', 'INVALID_PASSWORD'),
        401: authError('Not signed in.', 'UNAUTHORIZED'),
        403: authError('The session has not completed the second factor.', 'SECOND_FACTOR_REQUIRED'),
    },
});
export const disableTwoFactorRoute = createRoute({
    method: 'post',
    path: '/v1/auth/two-factor/disable',
    operationId: 'disableTwoFactor',
    tags: ['identity'],
    summary: 'Turn the second factor off',
    description: `${SESSION} The session must have completed the second factor. A staff member whose role requires one ` +
        '(`staff.require2faRoles`) is refused at the console until they set one up again.',
    request: json(TwoFactorPasswordRequestSchema),
    responses: {
        200: jsonResponse(TwoFactorStatusSchema, 'Off.'),
        400: authError('The password is wrong.', 'INVALID_PASSWORD'),
        401: authError('Not signed in.', 'UNAUTHORIZED'),
        403: authError('The session has not completed the second factor.', 'SECOND_FACTOR_REQUIRED'),
    },
});
/** The five, in the order a console walks through them. */
export const TWO_FACTOR_ROUTES = [
    enableTwoFactorRoute,
    verifyTwoFactorTotpRoute,
    verifyTwoFactorBackupCodeRoute,
    generateTwoFactorBackupCodesRoute,
    disableTwoFactorRoute,
];
//# sourceMappingURL=two-factor.js.map